import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { GiftEntity } from '@/domain/entities/gift/giftEntity';
import { ContentComponent, ContentComponentFullScreenSupport } from '@/presentation/feature/content/components';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import { useContentActions } from '@/presentation/feature/content/components/sheets/contentActions/hooks/useContentActions';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { Navigator } from '@/presentation/routing/Navigator';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { toggleUserMediaLikeUseCase } from '@/domain/usecases/content/toggleUserMediaLikeUseCase';
import { processFeedTagsWatchUseCase } from '@/domain/usecases/feedTags/processFeedTagsWatchUseCase';
import { updatePlaybackState } from '@/presentation/store/slices/videoManagement/videoManagementSlice';
import { UnlockContentSheet } from '../components/unlock/UnlockContentSheet';
import { SendGiftSheet } from '@/presentation/feature/gift/components/SendGiftSheet';
import { SendGiftTargetType } from '@/domain/enums/gift/sendGiftTargetType';
import { ContentCommentsSheet } from '../components/sheets/comments/ContentCommentsSheet';
import { ContentGiftsSheet } from '../components/sheets/contentGifts/ContentGiftsSheet';
import { ContentUsersLikedSheet } from '../components/sheets/contentUsersLiked/ContentUsersLikedSheet';
import { ContentActionsSheet } from '../components/sheets/contentActions/ContentActionsSheet';
import { UserActionsSheet } from '../components/sheets/userActions/UserActionsSheet';
import { AppealContentReportSheet } from '../components/sheets/appealContentReport/AppealContentReportSheet';
import type { RootState, AppDispatch } from '@/presentation/store';

export interface ContentListItemProps {
  media: UserMediaEntity;
  screenKey: string;
  currentUserId?: string;
  subscribed?: boolean;
  showMore?: boolean;
  showFullscreenButton?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  sheetContainer?: HTMLElement | null;
}

/**
 * Owns all per-media handlers wired into ContentComponent /
 * ContentComponentFullScreenSupport. Mirrors Flutter's BaseContentWidget
 * (lib/presentation/screens/content/custom_widgets/common/base_content_widget.dart),
 * which holds the same per-item state and callbacks at the row level.
 */
export const ContentListItem: React.FC<ContentListItemProps> = ({
  media,
  screenKey,
  currentUserId,
  subscribed = false,
  showMore = false,
  showFullscreenButton = false,
  autoplay = false,
  muted = false,
  sheetContainer,
}) => {
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const dateFormatters = useDateFormatters();
  const dispatch = useDispatch<AppDispatch>();
  const videoManagement = useVideoManagement();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const fullscreenState = useSelector((state: RootState) => state.videoManagement.fullscreen);
  const isFullscreen = fullscreenState.media?.id === media.id && fullscreenState.isFullscreen;

  const { publishDraft, publishScheduled } = useContentActions({ media });

  const onClick = useCallback((tappedMedia: UserMediaEntity) => {
    const videoKey = MediaUtils.composeVideoPlayerKey(screenKey, tappedMedia.id);
    videoManagement.initializeVideo(tappedMedia, videoKey, autoplay, { disposePrevious: true });
  }, [screenKey, autoplay, videoManagement]);

  const onError = useCallback((errorMedia: UserMediaEntity, error: Error) => {
    appLogger.error('Video error', 'ContentListItem.onError', { context: { mediaId: errorMedia.id }, error: error });
  }, []);

  const onUserTap = useCallback((userId: string) => {
    Navigator.navigateToUserDetails(userId);
  }, []);

  const onHashtagTap = useCallback((hashtag: string) => {
    Navigator.navigateToContentTag(hashtag);
  }, []);

  const onMentionTap = useCallback((mention: string) => {
    Navigator.navigateToUserDetailsByMention(mention);
  }, []);

  const onFullscreenClick = useCallback(async (clickedMedia: UserMediaEntity) => {
    const videoKey = MediaUtils.composeVideoPlayerKey(screenKey, clickedMedia.id);
    if (remoteConfigService.webNativeFullscreenMode) {
      await videoManagement.enterVideoFullscreen(videoKey);
      return;
    }
    await videoManagement.enterFullscreen(clickedMedia, videoKey);
  }, [screenKey, videoManagement]);

  const onFullscreenClose = useCallback(async () => {
    await videoManagement.exitFullscreen();
  }, [videoManagement]);

  const onVideoFullScreenStateChange = useCallback((stateMedia: UserMediaEntity, state: {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
  }) => {
    const videoKey = MediaUtils.composeVideoPlayerKey(screenKey, stateMedia.id);
    dispatch(updatePlaybackState({ videoKey, playbackState: state }));
  }, [screenKey, dispatch]);

  const onLikeTap = useCallback(async () => {
    if (!currentUser) return;
    // Mirror Flutter base_content_widget.dart:149-156 — when the current user
    // owns the media (content_view_model.dart:690-692 `isOwner()`), tapping like
    // opens the liked-users sheet and returns early instead of toggling the like.
    if (currentUser.firebaseId === media.firebaseId) {
      ContentUsersLikedSheet.show({
        mediaPath: media.relativePath,
        likes: media.likes ?? 0,
        container: sheetContainer,
        screenKey,
      });
      return;
    }
    try {
      const result = await toggleUserMediaLikeUseCase.execute(media, currentUser.firebaseId, currentUser.name);
      // Mirror Flutter content_view_model.dart:628-631 — process feed tags on like-success
      if (result.ok && result.liked) {
        void processFeedTagsWatchUseCase.execute(media.id);
      }
    } catch (error) {
      appLogger.error('Failed to toggle like', 'ContentListItem.onLikeTap', { context: { mediaId: media.id }, error: error as Error });
    }
  }, [media, currentUser, sheetContainer, screenKey]);

  const onUnlockContentTap = useCallback(() => {
    UnlockContentSheet.show(media, sheetContainer, screenKey);
  }, [media, sheetContainer, screenKey]);

  const onCommentTap = useCallback(() => {
    ContentCommentsSheet.show({ media, screenKey });
  }, [media, screenKey]);

  const onGiftTap = useCallback((defaultGift?: GiftEntity) => {
    // Mirror Flutter base_content_widget.dart:163-181: the owner's gift-button
    // tap opens the gifts-received list, everyone else opens the send-gift sheet.
    const isOwner = !!currentUser?.firebaseId && currentUser.firebaseId === media.firebaseId;
    if (isOwner) {
      ContentGiftsSheet.show({ mediaPath: media.relativePath, coins: media.coins ?? 0, container: sheetContainer, screenKey });
      return;
    }
    SendGiftSheet.show({ kind: SendGiftTargetType.media, media }, l10n.gift.sendGift, sheetContainer, screenKey, defaultGift);
  }, [media, sheetContainer, screenKey, l10n, currentUser]);

  const onShareTap = useCallback(() => {
    ContentActionsSheet.show(
      media,
      sheetContainer,
      screenKey,
      responsive.contentActionsSheet.fixedHeight,
    );
  }, [media, sheetContainer, screenKey, responsive]);

  const onMoreTap = useCallback((userId: string) => {
    // Content-list entry: deliberately omit the owner account-management items
    // (Edit profile, Settings, Help & About, Log out, Delete account) — those are
    // scoped to the UserDetailsPage entry, so `includeAccountActions` defaults to false.
    UserActionsSheet.show(
      undefined,
      userId,
      sheetContainer,
      screenKey,
      responsive.userActionsSheet.fixedHeight,
    );
  }, [sheetContainer, screenKey, responsive]);

  const onContentLinkTap = useCallback(() => {
    if (media.contentLink) {
      Navigator.navigateToContentDetailsByPath(media.contentLink);
    }
  }, [media.contentLink]);

  const onGet4kAccessTap = useCallback(() => {
    Navigator.navigateToPremiumPackages();
  }, []);

  const onNsfwTap = useCallback((tappedMedia: UserMediaEntity) => {
    const isOwn = currentUser?.firebaseId === tappedMedia.firebaseId;
    InfoSheet.show({
      title: l10n.content.nsfwInfoTitle,
      infoText: isOwn ? l10n.content.nsfwInfoMessageOwn : l10n.content.nsfwInfoMessage,
      screenKey,
    });
  }, [l10n, screenKey, currentUser?.firebaseId]);

  // Mirrors Flutter's `_onCommentReplyTap` (base_content_widget.dart:296):
  // navigate to the original media via its relative path, then let
  // ContentDetailsPage open the comments sheet at the highlighted parent.
  const onCommentReplyTap = useCallback(() => {
    const info = media.contentReplyCommentInfo;
    if (!info) return;
    Navigator.navigateToContentDetailsByPath(info.mediaPath, {
      commentInfo: {
        id: info.commentId,
        replyId: info.replyId,
        parentId: info.parentId,
        highlightParentComment: true,
      },
      openCommentsSheet: true,
    });
  }, [media.contentReplyCommentInfo]);

  const onContentBlockedTap = useCallback(() => {
    AppealContentReportSheet.show({
      contentId: media.id,
      container: sheetContainer,
      screenKey,
      fixedHeight: responsive.contentActionsSheet.fixedHeight,
    });
  }, [media.id, sheetContainer, screenKey, responsive]);

  const onDraftTap = useCallback(async () => {
    const success = await publishDraft();
    if (!success) toast.error(l10n.contentActions.publishDraftError);
    else toast.success(l10n.contentActions.publishDraftSuccess);
  }, [publishDraft, l10n]);

  const onScheduleTap = useCallback(async () => {
    if (media.schedule == null) return;
    const formattedDate = dateFormatters.formatDate(media.schedule, true);
    const description = l10n.contentActions.scheduledContentDescription.replace('{date}', formattedDate);

    let publishSucceeded = false;
    const response = await ConfirmSheet.show({
      title: l10n.contentActions.scheduledContentTitle,
      confirmText: description,
      okText: l10n.contentActions.publishNow,
      cancelText: l10n.common.close,
      screenKey,
      onConfirm: async () => {
        const success = await publishScheduled();
        if (success) publishSucceeded = true;
        else toast.error(l10n.contentActions.publishScheduledError);
      },
      loadingStyle: 'button',
    });

    if (response && publishSucceeded) {
      toast.success(l10n.contentActions.publishScheduledSuccess);
      SheetService.close();
    }
  }, [media.schedule, dateFormatters, l10n, screenKey, publishScheduled]);

  const props = {
    screenKey,
    media,
    autoplay,
    muted,
    currentUserId,
    subscribed,
    showMore,
    // Tag the row root with its entity id so the qa-tester can locate a
    // specific content card. The lock/premium `data-qa-status` is added inside
    // the content component, where the unlock state is already computed.
    qa: { id: `content-item-${media.id}` },
    onClick,
    onError,
    onFullscreenClick: showFullscreenButton ? onFullscreenClick : undefined,
    onVideoFullScreenStateChange,
    onUserTap,
    onHashtagTap,
    onMentionTap,
    onMoreTap,
    onLikeTap,
    onCommentTap,
    onGiftTap,
    onShareTap,
    onContentLinkTap,
    onCommentReplyTap,
    onGet4kAccessTap,
    onNsfwTap,
    onUnlockContentTap,
    onContentBlockedTap,
    onDraftTap,
    onScheduleTap,
  };

  if (remoteConfigService.webNativeFullscreenMode) {
    return <ContentComponent {...props} />;
  }

  return (
    <ContentComponentFullScreenSupport
      {...props}
      isFullscreen={isFullscreen}
      onFullscreenClose={onFullscreenClose}
    />
  );
};
